import useCustomer from 'src/app/customers/hooks/useCustomer';
import { useGetProductLicensingQuery } from 'src/app/admin/views/risk-portfolio/state/api/organisationEngineSlice';

import {
    asProductVisibilityOutcome,
    PRODUCT_RELEASE,
    ProductVisibilityMatrix,
    ProductKey,
    ProductVisibility,
    ProductVisibilityOutcome,
} from 'src/shared/types/engine/products';
import { ProductAccess } from 'src/shared/types/productLicensing';
import { isWithheld, productAccess } from 'src/shared/utils/productAccess';
import usePermissions from './usePermissions';

/**
 * The settings of the current organisation, and whether they are still to come.
 *
 * `currentData`, not `data`: while a new organisation's settings load, RTK
 * Query keeps the previous organisation's in `data`, and those must never
 * decide what shows. The settings are pending until they arrive, the request
 * fails, or the engine answers that it does not know the organisation (`null`).
 * A skipped request, before a customer resolves, is pending too.
 */
const useCurrentProductLicensing = (): { productLicensing: ProductVisibilityMatrix | null | undefined; isPending: boolean } => {
    const { currentCustomerId } = useCustomer();
    // Check error, not isError. RTK Query clears isError while a new subscriber refetches a failed query, but keeps error.
    const { currentData, error } = useGetProductLicensingQuery(currentCustomerId, { skip: !currentCustomerId });

    return { productLicensing: currentData, isPending: !currentCustomerId || (currentData === undefined && !error) };
};

/**
 * The setting of a product in product licensing management, with no internal
 * override folded in.
 *
 * `useProductAccess` gives the access of the current user, and internal staff
 * see the product in every mode. Staff need the configured mode to know what
 * the customer sees.
 *
 * `DEFAULT` means that the product has no setting.
 * `undefined` means that the settings are loading, or that the engine does
 * not know the organisation.
 */
export const useProductVisibility = (product: ProductKey): ProductVisibilityOutcome | undefined => {
    const { productLicensing } = useCurrentProductLicensing();

    if (!productLicensing) {
        return undefined;
    }
    return productLicensing[product] ? asProductVisibilityOutcome(productLicensing[product]) : ProductVisibilityOutcome.DEFAULT;
};

/** Each product has its own setting. The setting of one product does not change another. */
const isSetToHidden = (product: ProductKey, productLicensing: ProductVisibilityMatrix | null | undefined): boolean =>
    productLicensing?.[product] === ProductVisibility.HIDDEN;

/**
 * How much of a product the current user may see.
 *
 * Product licensing knows whether the results are released to this user. The
 * caller knows whether there are results (`hasResults`): the site's own data
 * for a Nature Performance product, `useHasRiskResults` for Global Nature Risk.
 * `productAccess` combines the two.
 *
 * An organisation the engine does not know, and an engine failure, have no
 * settings, so a product that releases automatically still shows its results.
 *
 * `LOADING` covers three windows: the settings of the current organisation
 * are still to come, no customer has resolved yet (the query is skipped and
 * reports no loading of its own), and the caller is still checking for
 * results (`hasResults` is `undefined`).
 */
export const useProductAccess = (product: ProductKey, hasResults: boolean | undefined): ProductAccess => {
    const { isCurrentUserInternal } = usePermissions();
    const { productLicensing, isPending } = useCurrentProductLicensing();

    if (isPending || hasResults === undefined) {
        return ProductAccess.LOADING;
    } else {
        return productAccess({
            release: PRODUCT_RELEASE[product],
            setting: isSetToHidden(product, productLicensing) ? ProductVisibility.HIDDEN : productLicensing?.[product],
            isInternal: isCurrentUserInternal,
            hasResults,
        });
    }
};

/**
 * Whether a surface outside a `ProductGate` must leave the product out for the
 * current user: it is set to Hidden for them, or the settings are still to
 * come. A hidden product shows no trace of itself, not even for a moment, so
 * the surface waits for the settings rather than showing and then removing it.
 */
export const useIsWithheldFromUser = (product: ProductKey): boolean => isWithheld(useProductAccess(product, false));

/**
 * Whether a product is set to Hidden for the current organisation. It ignores
 * who is looking, so internal staff, who still see the product, can tell that
 * customers do not.
 */
export const useIsSetToHidden = (product: ProductKey): boolean => {
    const { productLicensing } = useCurrentProductLicensing();

    return isSetToHidden(product, productLicensing);
};
